import { AnchorHTMLAttributes } from 'react';
import { SystemStyleObject } from '@styled-system/css';
import { createComponent, createHook } from 'reakit-system';
import invariant from 'tiny-invariant';
import themeGet from '@styled-system/theme-get';
import { useTheme } from '@emotion/react';
import { TextOptions, useText } from '../Text/Text';
import { cx, ecss } from '../reexports/emotion';
import { useStyles } from '../hooks/useStyles';

const linkStyles = {
  borderRadius: 4,
  color: 'linkColor',
  ':hover': {
    color: 'linkHoverColor',
  },
  ':active': {
    color: 'linkActiveColor',
  },
  ':visited': {
    color: 'linkVisitedColor',
    ':hover': {
      color: 'linkVisitedHoverColor',
    },
    ':active': {
      color: 'linkVisitedActiveColor',
    },
  },
} as SystemStyleObject;

function ColorIcon(
  isAnchor?: boolean,
  color: 'default' | 'hover' | 'active' = 'default'
) {
  let theme = useTheme();

  let colors = {
    default: themeGet('colors.linkColor')({ theme }) as string,
    hover: themeGet('colors.linkHoverColor')({ theme }) as string,
    active: themeGet('colors.linkActiveColor')({ theme }) as string,
  };

  let externalSvg = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path 
  fill-rule="evenodd" 
  clip-rule="evenodd" 
  d="M13.9999 4.75C13.9999 4.33579 14.3357 4 14.7499 4H19.2499C19.6642 4 19.9999 4.33579 19.9999 4.75V9.25C19.9999 9.66421 19.6642 10 19.2499 10C18.8357 10 18.4999 9.66421 18.4999 9.25V6.46231L11.568 13.3943C11.2751 13.6872 10.8002 13.6872 10.5073 13.3943C10.2144 13.1014 10.2144 12.6265 10.5073 12.3336L17.3409 5.5H14.7499C14.3357 5.5 13.9999 5.16421 13.9999 4.75ZM4.00381 7.67332C4.04222 7.29512 4.36161 7 4.74994 7H9.24994C9.66415 7 9.99994 7.33579 9.99994 7.75C9.99994 8.16421 9.66415 8.5 9.24994 8.5H5.49994V18.5H15.4999V14.75C15.4999 14.3358 15.8357 14 16.2499 14C16.6642 14 16.9999 14.3358 16.9999 14.75V19.25C16.9999 19.6642 16.6642 20 16.2499 20H4.74994C4.33573 20 3.99994 19.6642 3.99994 19.25V7.75C3.99994 7.72411 4.00125 7.69853 4.00381 7.67332Z" 
  fill="${colors[color]}"/>
</svg>`;

  const anchorSvg = `<svg
  xmlns="http://www.w3.org/2000/svg"
  width="1.5em"
  height="1.5em"
  fill="none"
  viewBox="0 0 24 24"
  >
  <path
    fill="${colors[color]}"
    fillRule="evenodd"
    d="M17.317 4.757l1.676 1.676c1.223 1.223 1.166 3.256-.12 4.542L16 13.847c-1.286 1.286-3.319 1.343-4.542.12l-1.117-1.117a.758.758 0 011.072-1.072l1.116 1.117c.626.626 1.702.578 2.4-.12L17.8 9.903c.698-.697.746-1.773.12-2.399L16.246 5.83c-.626-.626-1.702-.578-2.4.12l-2.22 2.221a.758.758 0 01-1.072-1.072l2.221-2.22c1.286-1.287 3.319-1.344 4.542-.12zM13.408 10.9l-1.117-1.116c-1.224-1.224-3.256-1.167-4.542.12l-2.872 2.871c-1.286 1.286-1.343 3.319-.12 4.542l1.676 1.676c1.223 1.223 3.256 1.166 4.542-.12l2.221-2.221a.758.758 0 00-1.072-1.072l-2.22 2.221c-.698.698-1.774.746-2.4.12L5.83 16.246c-.626-.626-.578-1.702.12-2.4l2.872-2.871c.697-.698 1.773-.746 2.399-.12l1.116 1.117a.758.758 0 001.072-1.072z"
    clipRule="evenodd"
  />
</svg>`;

  const svgIcon = isAnchor ? anchorSvg : externalSvg;

  return `url('data:image/svg+xml, ${encodeURIComponent(svgIcon)}')`;
}

function linkIconStyles(isAnchor?: boolean) {
  return {
    borderRadius: 4,
    paddingRight: '1.5em',
    backgroundImage: ColorIcon(isAnchor),
    backgroundSize: '1.5em 1.5em',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right center',

    ':hover': {
      color: 'linkHoverColor',
      backgroundImage: ColorIcon(isAnchor, 'hover'),
    },

    ':active': {
      color: 'linkActiveColor',
      backgroundImage: ColorIcon(isAnchor, 'active'),
    },
  };
}

type LinkModificator =
  | {
      anchor: true;
      external?: false;
    }
  | {
      anchor?: false;
      external: true;
    }
  | { anchor?: false; external?: false };

export type LinkOptions = LinkModificator & TextOptions;

export type LinkHTMLProps = AnchorHTMLAttributes<HTMLAnchorElement>;

export type LinkProps = LinkHTMLProps & LinkOptions;

export const useLink = createHook<
  LinkOptions,
  AnchorHTMLAttributes<HTMLAnchorElement> & LinkModificator
>({
  name: 'Link',
  compose: [useStyles, useText],
  keys: ['external', 'anchor'],
  useOptions(options) {
    //
    invariant(
      !(options.external && options.anchor),
      'Используйте anchor или external, возможен только один из вариантов'
    );

    let IconStyles = linkIconStyles(options.anchor);
    let extStyles = options.external || options.anchor ? IconStyles : {};
    return {
      ...options,
      styles: {
        ...options.styles,
        ...linkStyles,
        ...extStyles,
        textDecoration: 'underline',
        '&:focus, &:focus-visible': {
          outlineStyle: 'solid',
          outlineWidth: '4px',
          outlineOffset: '0',
          outlineColor: 'focus',
          borderRadius: '4px',
        },
      },
    };
  },
  useProps(options, props) {
    let { target } = props;

    if (!target && options.external) {
      target = '_blank';
    }

    return {
      ...props,
      className: cx(props.className, ecss({ label: 'Link' })),
      target,
    };
  },
});

/**
 * @beta
 */
export const Link = createComponent({
  as: 'a',
  useHook: useLink,
  memo: true,
});
